import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { getRelativeTime } from 'webapp/utils/number';

import { AiSummaryContentStatus } from 'webapp/components/block/content/ai-summary';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';

export default class CommonPopupsAiRegenerateComponent extends PopupInnerContextComponent<PopupData.Common.AiRegenerate> {
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummaries: Service.ModelManager.DataPerformanceSummaries;

	@tracked isRegenerateAiSummaryInProgress = false;

	get lastSync() {
		return this.popupData.lastSync;
	}

	get generativeAiDailyUserLimitRemaining() {
		return this.mmDataPerformanceSummaries.dailyUserLimitRemaining;
	}

	get isGenerativeAiDailyUserLimitReached() {
		return !this.generativeAiDailyUserLimitRemaining;
	}

	get generativeAiLimitReachedMessage() {
		return this.mmDataPerformanceSummaries.limitReachedMessage;
	}

	get isGenerativeAiLimitReached() {
		return this.mmDataPerformanceSummaries.limitReached;
	}

	get showLearnMore(): boolean {
		return !this.accountService.hideHelpArticles && !this.navigationService.isMobileApp;
	}

	get relativeTimeText() {
		return getRelativeTime(this.lastSync!);
	}

	get canRegenerateAiSummary() {
		return this.popupData.canRegenerateAiSummary;
	}

	get isEdited() {
		return this.popupData.aiSummaryContentStatus === AiSummaryContentStatus.EDITABLE;
	}

	get syncDescriptionText() {
		if (this.isEdited) {
			return this.editedDescriptionText;
		} else {
			return this.regenerateDescriptionText;
		}
	}

	get regenerateDescriptionText() {
		let text = '';
		text = `This summary was generated ${this.relativeTimeText}.`;
		if (this.canRegenerateAiSummary) {
			text += '<br>';
			if (this.isGenerativeAiDailyUserLimitReached) {
				text += '<span class="red">Daily regenerate limit reached.</span>';
			} else {
				text += `Daily regenerate limit remaining: ${this.generativeAiDailyUserLimitRemaining}`;
			}
		}

		return text;
	}

	get editedDescriptionText() {
		let text = '';

		if (!this.isEdited || !this.popupData.editedAt || !this.popupData.editedBy) {
			return;
		}

		text = `Edited ${getRelativeTime(this.popupData.editedAt)} by ${this.popupData.editedBy}`;

		if (this.canRegenerateAiSummary) {
			text += `<br>Daily regenerate limit remaining: ${this.generativeAiDailyUserLimitRemaining}`;
		}

		return text;
	}

	get aiSummaryContentStatusTitle() {
		// check account limits and block status
		if (
			this.popupData.aiSummaryContentStatus === AiSummaryContentStatus.LIMIT_REACHED ||
			this.isGenerativeAiLimitReached
		) {
			return 'Limit reached';
		} else if (this.popupData.aiSummaryContentStatus === AiSummaryContentStatus.ERROR) {
			return 'Unexpected error occured';
		} else if (this.popupData.aiSummaryContentStatus === AiSummaryContentStatus.NO_DATA) {
			return 'No data to display';
		}
		return;
	}

	get aiSummaryContentStatusDescription() {
		// check account limits and block status
		if (
			this.popupData.aiSummaryContentStatus === AiSummaryContentStatus.LIMIT_REACHED ||
			this.isGenerativeAiLimitReached
		) {
			return 'Daily limit reached for regenerating AI summaries. Please try again tomorrow.';
		} else if (this.popupData.aiSummaryContentStatus === AiSummaryContentStatus.ERROR) {
			return 'We are unable to load performance insights at the moment. Please give it another try shortly.';
		} else if (this.popupData.aiSummaryContentStatus === AiSummaryContentStatus.NO_DATA) {
			return 'We are unable to load performance insights due to missing data for the selected date range.';
		}
		return;
	}
	<template>
		<div class="ct icon-info-section">
			<CommonUiIconComponent @name="InfoFill" @size="sm" />
			<strong>About</strong>
			<p>
				Performance summary was generated with
				{{#unless this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins}}
					Databox
				{{/unless}}
				AI.
			</p>
			<div class="actions">
				{{#if this.showLearnMore}}
					<a
						href="https://help.databox.com/databox-generative-ai"
						target="_blank"
						class="more"
					>
						Learn more
					</a>
				{{/if}}
			</div>
		</div>

		<div class="ct icon-info-section">
			<CommonUiIconComponent @name="InfoFill" @size="sm" />
			<strong>Last updated</strong><br />
			<p>{{{this.syncDescriptionText}}}</p>
		</div>

		{{#if this.aiSummaryContentStatusDescription}}
			<div class="ct alert-section">
				<CommonUiIconComponent @name="AlertFill" @size="sm" />
				<strong>{{this.aiSummaryContentStatusTitle}}</strong><br />
				<p>{{{this.aiSummaryContentStatusDescription}}}</p>
			</div>
		{{/if}}
	</template>
}
